import * as React from 'react';
import { useEffect } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { Box, Drawer, Typography } from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { ChevronLeft } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Industrial_Sensors_Products_ID } from './Product';
import { MenuItemData } from '../../common/SubMenu';
import { useDim } from '../../../contexts/DimContext';
import Image from 'next/image';

export function isMenuItemData(item: any): item is MenuItemData {
    return item && typeof item.title === 'string' && typeof item.level === 'number';
}

export function isArrayOfMenuItemData(items: any[]): items is MenuItemData[] {
    return items.every(isMenuItemData);
}

type SubMenuProps<T extends MenuItemData> = {
    isMenuOpen: boolean;
    anchorEl: (EventTarget & Element) | null | HTMLElement;
    menuLevels: number;
    menuItemsData: T[];
    onClose: (menuItem?: T) => void;
    setMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | (EventTarget & Element) | null>>;
    handleNavClose?: () => void;
    mainMenuName?: string;
};

const menuItemTitleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: '400',
    fontFamily: 'Poppins',
    lineHeight: '2rem',
    color: '#B75332',
    border: 'none',
    display: 'flex',
    justifyContent: 'space-between',
};

const DRAWER_TOP = 95;

function NavSubMenu<T extends MenuItemData>({
    isMenuOpen,
    anchorEl,
    menuItemsData,
    menuLevels,
    onClose,
    setMenuOpen,
    setAnchorEl,
    handleNavClose,
}: SubMenuProps<T>) {
    const { acquire, release } = useDim();

    const [anchors, setAnchors] = React.useState<{
        elements: Array<null | HTMLElement>;
        menuItems: Array<null | typeof menuItemsData>;
    }>({
        elements: new Array(menuLevels).fill(null),
        menuItems: new Array(menuLevels).fill(null),
    });

    const [parentMenuItem, setParentMenuItem] = React.useState<T | null>(null);
    const navigate = useNavigate();
    const [openSubMenu, setOpenSubMenu] = React.useState<boolean>(true);

    const handleOpen = (
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        level = 0,
        nestedMenuItems: T[] = menuItemsData
    ) => {
        const target = event.currentTarget as HTMLElement;

        setAnchors((prevAnchors) => ({
            elements: prevAnchors.elements.map((element, index) =>
                index === level ? target : element
            ),
            menuItems: prevAnchors.menuItems.map((element, index) =>
                index === level ? nestedMenuItems : element
            ),
        }));
    };

    const handleClose = (menuItemData?: T) => {
        onClose(menuItemData);
    };

    const open = Boolean(anchorEl);

    const openItem = (menuItem: T) => {
        const route = menuItem.route;
        if (!route) {
            console.error('Menu item does not have a route defined');
            return;
        }
        if (route.startsWith('http')) {
            window.open(route, '_blank');
            handleNavClose && handleNavClose();
            return;
        }

        navigate(route, { state: { shouldClearAllProps: true } });
        handleClose(menuItem);
        handleNavClose && handleNavClose();
    };

    const handleClickMenuItem = (
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        menuItem: T
    ) => {
        if (menuItem.children && menuItem.children.length > 0) {
            if (isArrayOfMenuItemData(menuItem.children)) {
                setOpenSubMenu(true);
                setParentMenuItem(menuItem);
                handleOpen(event, menuItem.level + 1, menuItem.children as T[]);
            } else {
                // Handle case where children are not of type T[]
                console.error('Children are not of expected type');
            }
            return;
        }

        openItem(menuItem);
    };

    const handleClickSubmenuHeader = () => {
        if (!parentMenuItem || !parentMenuItem.title || !parentMenuItem.route) {
            console.error('Parent menu item is not defined');
            return;
        }
        openItem(parentMenuItem);
    };

    const handleGoBack = () => {
        if (isMenuOpen && openSubMenu) {
            setOpenSubMenu(false);
            setParentMenuItem(null);
        } else {
            setMenuOpen(false);
        }
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
        handleGoBack();
        setMenuOpen(false);
    };

    useEffect(() => {
        if (!isMenuOpen) return;
        acquire(); // turn ON (increments counter)
        return () => release(); // paired release when submenu closes/unmounts
    }, [isMenuOpen, acquire, release]);

    const maskUrl = '/externalLink.svg';

    return (
        <>
            <Drawer
                anchor="right"
                id="long-menu"
                open={open}
                onClose={handleNavClose}
                PaperProps={{
                    sx: {
                        top: DRAWER_TOP,
                        width: '100%',
                        maxWidth: '100%',
                        left: 0,
                        right: 0,
                        position: 'fixed',
                        maxHeight: `calc(100vh - ${DRAWER_TOP}px)`,
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        display: 'flex', // ★ 變成 flex 容器
                        flexDirection: 'column', //   垂直排列（header + ul）
                        minHeight: 0,
                    },
                    elevation: 0,
                }}
                BackdropProps={{ style: { backgroundColor: 'transparent' } }}
            >
                <ul className="nav-button-list">
                    <MenuItem
                        onClick={handleMenuClose}
                        sx={{
                            backgroundColor: '#F2F2F2',
                            display: 'flex',
                            justifyContent: 'space-between',
                            paddingLeft: '0.5rem',
                            paddingRight: '0.5rem',
                        }}
                    >
                        <ChevronLeft
                            sx={{
                                fontSize: '1rem',
                                transition: 'transform 0.3s ease',
                                justifyContent: 'flex-start',
                            }}
                        />
                        <Typography
                            sx={{
                                gap: '1.25rem',
                                fontFamily: 'Poppins',
                                fontWeight: '400',
                                lineHeight: '33px',
                                fontSize: '16px',
                                marginTop: '0.1rem',
                            }}
                        >
                            Go Back
                        </Typography>
                    </MenuItem>

                    {(anchors.menuItems[0] ?? menuItemsData).map((item) => (
                        <React.Fragment key={item.id}>
                            <MenuItem
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    color: 'black',
                                    paddingLeft: '0.5rem',
                                    paddingRight: '0.5rem',
                                }}
                                aria-haspopup={!!item.children}
                                aria-expanded={
                                    item.children
                                        ? anchors.elements.some(
                                              (element) => element?.innerText === item.title
                                          )
                                        : undefined
                                }
                                onClick={(event) => {
                                    handleClickMenuItem(event, item);
                                }}
                            >
                                <Typography
                                    sx={{
                                        gap: '1.25rem',
                                        fontFamily: 'Poppins',
                                        fontWeight: '400',
                                        lineHeight: '33px',
                                        fontSize: '16px',
                                        marginTop: '0.1rem',
                                    }}
                                >
                                    {item.title}
                                </Typography>
                                {Array.isArray(item.children) && item.children.length > 0 && (
                                    <ChevronRight
                                        fontSize="small"
                                        className="chevron-right"
                                        sx={{
                                            transition: 'transform 0.3s ease',
                                        }}
                                    />
                                )}
                            </MenuItem>
                            <hr
                                style={{
                                    width: '100%',
                                    fontWeight: 700,
                                    color: '#CACBCD',
                                }}
                            />
                        </React.Fragment>
                    ))}
                </ul>
            </Drawer>
            {openSubMenu &&
                anchors.elements.map((anchorElement, index) =>
                    anchorElement ? (
                        <Drawer
                            key={index}
                            id="long-menu"
                            anchor="right"
                            open={openSubMenu}
                            onClose={handleNavClose}
                            PaperProps={{
                                sx: {
                                    top: DRAWER_TOP,
                                    width: '100%',
                                    maxWidth: '100%',
                                    left: 0,
                                    right: 0,
                                    position: 'fixed',
                                    maxHeight: `calc(100vh - ${DRAWER_TOP}px)`,
                                    overflowY: 'auto',
                                    WebkitOverflowScrolling: 'touch',
                                    display: 'flex', // ★ 變成 flex 容器
                                    flexDirection: 'column', //   垂直排列（header + ul）
                                    minHeight: 0,
                                },
                                elevation: 0,
                            }}
                            BackdropProps={{
                                style: { backgroundColor: 'transparent' },
                            }}
                        >
                            <ul className="nav-button-list">
                                <MenuItem
                                    onClick={handleGoBack}
                                    sx={{
                                        backgroundColor: '#F2F2F2',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <ChevronLeft
                                        sx={{
                                            fontSize: '1rem',
                                            transition: 'transform 0.3s ease',
                                            justifyContent: 'flex-start',
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            gap: '1.25rem',
                                            fontFamily: 'Poppins',
                                            fontWeight: '400',
                                            lineHeight: '33px',
                                            fontSize: '16px',
                                            marginTop: '0.1rem',
                                        }}
                                    >
                                        Go Back
                                    </Typography>
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        handleClickSubmenuHeader();
                                    }}
                                    sx={menuItemTitleStyle}
                                >
                                    {parentMenuItem?.title}
                                </MenuItem>
                                <hr
                                    style={{
                                        width: '100%',
                                        fontWeight: 700,
                                        color: '#CACBCD',
                                    }}
                                />
                                {(anchors.menuItems[index] ?? []).map((item, _itemIndex) => (
                                    <React.Fragment key={`${item.title}-${_itemIndex}`}>
                                        <MenuItem
                                            key={item.title}
                                            aria-haspopup={!!item.children}
                                            aria-expanded={
                                                item.children
                                                    ? anchors.elements.some(
                                                          (element) =>
                                                              element?.innerText === item.title
                                                      )
                                                    : undefined
                                            }
                                            onClick={(event) => {
                                                handleClickMenuItem(event, item);
                                            }}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Box
                                                data-testid={`${item.title}-nav`}
                                                sx={{
                                                    gap: '1.25rem',
                                                    fontFamily: 'Poppins',
                                                    fontWeight: '400',
                                                    lineHeight: '33px',
                                                    fontSize: '16px',
                                                    marginTop: '0.1rem',
                                                }}
                                                component={'a'}
                                            >
                                                <Typography
                                                    sx={{
                                                        gap: '1.25rem',
                                                        lineHeight: '118.8%',
                                                        fontFamily: 'Poppins',
                                                        fontWeight: '400',
                                                        marginTop: '0.1rem',
                                                    }}
                                                >
                                                    {item.title}
                                                    {item.id &&
                                                        parseInt(item.id) ===
                                                            Industrial_Sensors_Products_ID && (
                                                            <Image
                                                                src={maskUrl}
                                                                alt="external link"
                                                                width={16}
                                                                height={16}
                                                                style={{
                                                                    marginLeft: '0.5rem',
                                                                    verticalAlign: 'middle',
                                                                }}
                                                            />
                                                        )}
                                                </Typography>
                                                {item.children && item.children.length > 0 && (
                                                    <ChevronRight fontSize="small" />
                                                )}
                                            </Box>
                                        </MenuItem>
                                        <hr
                                            style={{
                                                width: '100%',
                                                fontWeight: 700,
                                                color: '#CACBCD',
                                            }}
                                        />
                                    </React.Fragment>
                                ))}
                            </ul>
                        </Drawer>
                    ) : null
                )}
        </>
    );
}

export default NavSubMenu;
